// ----------------------------------------------------------------------------
// Copyright (c) Ben Coleman, 2020
// Licensed under the MIT License.
//
// Set of methods to call the Graph API, using REST and fetch
// ----------------------------------------------------------------------------

import axios from 'axios'
import auth from './auth'
import { store } from "@/vuex/store.js"


const GRAPH_BASE = 'https://graph.microsoft.com/v1.0'
const GRAPH_SCOPES = [ 'user.readwrite.all','sites.readwrite.all','calendars.readwrite','files.readwrite.all','directory.readwrite.all','directory.read.all','people.read','people.read.all', 'group.readwrite.all', 'group.read.all','sites.fullcontrol.all' ]

let accessToken

export default {
  //
  // Get details of user, and return as JSON
  // https://docs.microsoft.com/en-us/graph/api/user-get?view=graph-rest-1.0&tabs=http#response-1
  //
  async getSelf() {
    let resp = await callGraph('/me')
    if (resp) {
      let data = await resp.json()
      // console.log(data)
      return data
    }
  },

  //
  // Get user's photo and return as a blob object URL
  // https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL
  //
  async getPhoto(format = 'url') {
    try {
      const resp = await callGraph('/me/photos/240x240/$value')
      if (resp) {
        const blob = await resp.blob()
        if (format === 'dataUrl') {
          return await blobToDataURL(blob)
        }
        return URL.createObjectURL(blob)
      }
      return null
    } catch (e) {
      return null
    }
  },

  // Get another user's photo by Azure AD objectId or UPN (email)
  // Returns a blob URL by default, or base64 data URL when format==='dataUrl'
  async getUserPhoto(userIdOrUpn, { size = '240x240', format = 'url' } = {}) {
    if (!userIdOrUpn) return null
    console.log("userIdOrUpn", userIdOrUpn);
    const sizedPath = `/users/${userIdOrUpn}/photo/$value`
    try {
      let resp = await callGraph(sizedPath)
      if (resp && resp.ok) {
        const blob = await resp.blob()
        console.log("blob", blob);
        if (format === 'dataUrl') {
          return await blobToDataURL(blob)
        }
        return URL.createObjectURL(blob)
      }
      return null
    } catch (e) {
      return null
    }
  },
  async getInboxEmails(accessToken, next_link, filter, folderName) {
    let resp = await callGraph(next_link == null ? '/me/mailFolders/'+ folderName +'/messages/?'+filter+'&$select=sender,subject,createdDateTime,hasAttachments,isRead' : next_link, next_link == null)
    if (resp) {
      let data = await resp.json()
      // console.log("Got data ", data)
      return data
    }

    return data;
  },
  async getMixedEmails(accessToken, next_link, filter) {
    let resp = await callGraph(next_link == null ? '/me/messages/?'+filter+'&$select=sender,subject,createdDateTime,hasAttachments,isRead' : next_link, next_link == null)
    if (resp) {
      let data = await resp.json()
      // console.log("Got data ", data)
      return data
    }

    return data;
  },
  async getUnreadEmailCount() {
    try {
      // Get unread emails from last 30 days only (more relevant, less computational)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const dateFilter = thirtyDaysAgo.toISOString();

      // Use $count and $top=1 to get count without fetching all messages
      const filter = `isRead eq false and receivedDateTime ge ${dateFilter}`;
      let resp = await callGraph(`/me/mailFolders/inbox/messages?$filter=${encodeURIComponent(filter)}&$count=true&$top=1`, true, {
        'ConsistencyLevel': 'eventual'
      })
      if (resp) {
        let data = await resp.json()
        return data['@odata.count'] || 0
      }
      return 0
    } catch (e) {
      console.error('Error fetching unread email count:', e)
      return 0
    }
  },
  async markEmailAsRead(emailId) {
    try {
      accessToken = store.state.aToken;
      const resp = await fetch(`${GRAPH_BASE}/me/messages/${emailId}`, {
        method: 'PATCH',
        headers: {
          authorization: `bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isRead: true })
      });
      return resp.ok;
    } catch (e) {
      console.error('Error marking email as read:', e);
      return false;
    }
  },
  async getSingleFile(groupId, itemId) {
    let resp = await callGraph("/groups/" + groupId + "/drive/items/" + itemId)
    if (resp) {
      let data = await resp.json()
      // console.log(data)
      return data
    }
  },
  async  getMultipleFiles(links) {
    const files = await Promise.all(links.map(async (link) => {
      const resp = await callGraph(link);
      if (resp) {
        const data = await resp.json();
        return data;
      }
      return null; // In case the response is not successful, you may want to handle this differently
    }));
    return files.filter(file => file !== null); // Filter out any null values in case of unsuccessful responses
  },

    async getThumbnails(groupId, itemId) {
    let resp = await callGraph(`/groups/${groupId}/drive/items/${itemId}/thumbnails`)
    if (resp) {
      let data = await resp.json()
      return data
    }
  },

  async getFileChildren(groupId, itemId) {
    let resp = await callGraph("/groups/" + groupId + "/drive/items/" + itemId+"/children?$expand=thumbnails&$top=1000")
    if (resp) {
      let data = await resp.json()
      return data
    }
  },
  async searchFilesInGroup(groupId, srch_txt) {
    let resp = await callGraph("/groups/" + groupId + "/drive/root/search(q='{"+srch_txt+"}')")
    if (resp) {
      let data = await resp.json()
      return data
    }
  },

  async getFileByPath(groupId, path) {
    let resp = await callGraph("/groups/" + groupId + "/drive/" + path)
    if (resp) {
      let data = await resp.json()
      // console.log("looking for response", data.id)
      return data.id
    }
  },

  async checkFolderContainsFile(groupId, itemId, search_name) {
    
    let resp = await callGraph(`/groups/${groupId}/drive/items/${itemId}/children?$filter=startswith(name,'${search_name}')`);
    if (resp) {
      let data = await resp.json()
      // console.log("Found searched files by name", data)
      return data
    }
  },


  //
  // Search for users
  // https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL
  //
  async searchUsers(searchString, max = 50) {
    let resp = await callGraph(`/users?$filter=startswith(displayName, '${searchString}') or startswith(userPrincipalName, '${searchString}')&$top=${max}`)
    if (resp) {
      let data = await resp.json()
      return data
    }
  },

  //
  // Accessor for access token, only included for demo purposes
  //
  getAccessToken() {
    return accessToken
  },


  async copyFileBetweenGroups(sourceGroupId, fileId, destinationGroupId, destinationFolderId) {
    const copyUrl = `/groups/${sourceGroupId}/drive/items/${fileId}/copy`;

    accessToken = store.state.aToken;
  
    const body = {
      "parentReference": {
        "driveId": destinationGroupId,
        "id": destinationFolderId // Use the folder ID as the destination reference
      },
      "@microsoft.graph.conflictBehavior":'rename'
    };
  console.log(accessToken);
    const response = await fetch(`${GRAPH_BASE}${copyUrl}`, {
      method: 'POST',
      headers: {
        authorization: `bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
  
    if (!response.ok) {
      throw new Error(`Failed to copy file: ${response.statusText}`);
    }
  
    return response.status === 202; // 202 Accepted for async operation
  }
}




//
// Common fetch wrapper (private)
//
async function callGraph(apiPath, no_base=true, extraHeaders={}) {
  // Acquire an access token to call APIs (like Graph)
  // Safe to call repeatedly as MSAL caches tokens locally
  accessToken = store.state.aToken;

  let resp = await fetch(
    `${no_base ? GRAPH_BASE: ''}${apiPath}`,
    {
      headers: {
        authorization: `bearer ${accessToken}`,
        ...extraHeaders
      }
    }
  )

  if (!resp.ok) { throw new Error(`Call to ${GRAPH_BASE}${apiPath} failed: ${resp.statusText}`) }

  return resp
}

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}